import { hexToLinearBytes } from '@/utils/colorSpace'
import { usesLegacyVoxelPipeline } from '@/voxels/pipeline'

export type PackedPreview = {
  storage: Uint32Array
  count: number
  version: number
  keys?: number[]
  bounds?: { x: number; y: number; z: number }
  // Optional translation applied after centering/scaling when fitting into palette space.
  originOffset?: [number, number, number]
  // Optional hint to center/fit the preview content into a single world unit.
  fitWithinUnit?: boolean
  // Optional visual calibration applied around the preview center after fitting.
  previewTransform?: PackedPreviewTransform
}

export type PackedPreviewTransform = {
  scale?: number
  offset?: [number, number, number]
  rotation?: [number, number, number]
}

export type PackedPreviewBuilder = (color: number) => PackedPreview | null
export type SingleVoxelPackedPreviewOptions = Partial<Pick<
  PackedPreview,
  'bounds' | 'fitWithinUnit' | 'keys' | 'originOffset' | 'previewTransform' | 'version'
>> & {
  data?: number
}

import { getVoxelSpec } from './registry'

export function singleVoxelPackedPreview(colorHex: number, options: SingleVoxelPackedPreviewOptions = {}): PackedPreview {
  const { data = 0, version = 1, ...previewOptions } = options
  const [lr, lg, lb] = hexToLinearBytes(colorHex)
  const packedColor = ((lb & 0xff) << 16) | ((lg & 0xff) << 8) | (lr & 0xff)
  return {
    storage: new Uint32Array([
      0,
      255 << 24,
      packedColor,
      data,
    ]),
    count: 1,
    version,
    ...previewOptions,
  }
}

export function buildPackedPreviewStorage(type: string, colorHex: number) {
  const builder = getVoxelSpec(type)?.preview
  if (builder) return builder(colorHex)
  if (usesLegacyVoxelPipeline(type)) return null
  return singleVoxelPackedPreview(colorHex)
}
